import { RETURN_DISPOSAL_REQUESTABLE_ACTIONS, type TypeReturnDisposalMatrixAction } from '~/constants';

import type { TypeReturnDisposalInfo } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 처리신청 버튼 차단 판정.
 *
 * 목록에서 선택 자체가 가능한 카드라도 액션에 따라 신청이 불가하다. 서버가 허용하지 않는
 * 처리방법·처리상태 조합을 "올바른 처리방법/처리상태가 아닙니다"로 거부하므로, 버튼 단계에서
 * 걸러 사용자가 이유를 알 수 없는 신청 실패를 보지 않게 한다.
 *
 * 액션별 허용 조합은 전환 매트릭스(RETURN_DISPOSAL_REQUESTABLE_ACTIONS) 하나가 정본이다 —
 * 상태 조합을 가드마다 흩뿌리면 다음 매트릭스 변경에서 또 어긋난다.
 *
 * 신청 기한(이슈등록일 D+3) 초과 판정은 경과일수를 쓰므로 호출부에 둔다.
 */

/** 전환 매트릭스에서 액션이 막히는 카드가 섞여 있는지 판정한다. 매트릭스에 없는 조합도 신청 불가로 본다. */
const hasMatrixBlockedRow = (rows: TypeReturnDisposalInfo[], action: TypeReturnDisposalMatrixAction): boolean =>
  rows.some((row) => {
    const allowedActions = RETURN_DISPOSAL_REQUESTABLE_ACTIONS[`${row.processMethodCode}:${row.processStatus}`] ?? [];

    return !allowedActions.includes(action);
  });

/**
 * 회수(택배·직배) 신청이 막히는 선택인지 판정한다.
 *
 * - 회송(진행중): 회수 정보를 다시 받지 않는다 — 폐기나 클레임으로만 전환할 수 있다.
 * - 보류·재입고: 컬리 검토 또는 재발주 입고를 기다리는 단계라 화주사 신청 대상이 아니다.
 */
export const isRtsRequestBlocked = (rows: TypeReturnDisposalInfo[]): boolean => hasMatrixBlockedRow(rows, 'RTS');

/**
 * 폐기 신청이 막히는 선택인지 판정한다.
 *
 * 보류·재입고 카드가 섞이면 막는다. 회송은 진행중·정보입력대기 모두 폐기로 전환할 수 있다.
 */
export const isScrapRequestBlocked = (rows: TypeReturnDisposalInfo[]): boolean => hasMatrixBlockedRow(rows, 'SCRAP');

/** 컬리나우 센터 카드 포함 여부 — 컬리나우는 전통주만 매입해 폐기 신청을 막는다. 플래그가 없으면 일반 센터로 본다. */
export const hasKurlyNowCenterRow = (rows: TypeReturnDisposalInfo[]): boolean =>
  rows.some((row) => row.isKurlyNowCenter === true);

/**
 * 클레임 신청이 막히는 선택인지 판정한다.
 *
 * - 클레임신청: 이슈카드당 1회 제한. 목록 응답에 클레임 이력 플래그가 따로 없고, 반려되면 처리방법이
 *   신청 직전 값으로 돌아가고 승인되면 보류가 되므로 그 두 경우의 이력은 알 수 없다 — 처리방법이
 *   클레임신청인 카드만 걸러내고, 이력 플래그가 제공되면 그 값으로 대체한다.
 * - 회송(정보입력대기): 보류 심사를 거쳐 회송으로 확정한 건이라 다시 이의를 받지 않는다.
 * - 보류·재입고: 화주사 신청 대상이 아니다.
 */
export const isClaimRequestBlocked = (rows: TypeReturnDisposalInfo[]): boolean => hasMatrixBlockedRow(rows, 'CLAIM');

/** `yyyy-MM-dd'T'HH:mm:ss` 에서 날짜(`yyyy-MM-dd`)만 잘라내는 길이 */
const DATE_ONLY_LENGTH = 10;

/** 보류해제-회송 카드: 컬리 담당자가 회수예정일을 이미 지정한 건이라 회수 신청 시 날짜 변경이 불가하다. */
const isRtsPendingInputRow = (row: TypeReturnDisposalInfo): boolean =>
  row.processMethodCode === 'RTS' && row.processStatus === 'PENDING_INPUT';

/** 선택 전건이 보류해제-회송인지 판정한다. 회수 신청 모달이 지정일 고정 variant로 열려야 하는 조건. */
export const isRtsPendingInputOnlySelection = (rows: TypeReturnDisposalInfo[]): boolean =>
  rows.length > 0 && rows.every(isRtsPendingInputRow);

/**
 * 보류해제-회송 카드와 일반 카드가 섞였는지 판정한다.
 *
 * 회수 신청은 한 번에 하나의 회수예정일로 접수되는데, 지정일이 강제되는 카드와 자유 선택
 * 카드를 한 신청으로 묶으면 어느 쪽 날짜를 따를지 정할 수 없어 신청 진입을 막는다.
 */
export const isRtsPendingInputMixedSelection = (rows: TypeReturnDisposalInfo[]): boolean =>
  rows.some(isRtsPendingInputRow) && !rows.every(isRtsPendingInputRow);

/**
 * 보류해제-회송 카드끼리 컬리 지정 회수예정일이 서로 다른지 판정한다.
 *
 * 회수 신청은 한 번에 하나의 회수예정일로 접수되므로 지정일이 다른 카드는 묶을 수 없다.
 * 값은 일시(`yyyy-MM-dd'T'HH:mm:ss`)로 내려오지만 시간대는 센터 입고가능시간 슬롯이 정하므로
 * 날짜만 비교한다 — 같은 날짜면 한 슬롯으로 접수된다.
 */
export const isRtsPlannedAtMismatchSelection = (rows: TypeReturnDisposalInfo[]): boolean =>
  new Set(rows.filter(isRtsPendingInputRow).map((row) => row.rtsPlannedAt?.slice(0, DATE_ONLY_LENGTH) ?? null)).size >
  1;
